/**
 * Проверка звука прямо в пульте, до первого звонка клиенту.
 *
 * Зачем отдельно от эхо-теста на станции: эхо-тест проверяет весь путь разом
 * и на отказе не говорит, где порвалось. Здесь три независимых проверки, и
 * каждая отвечает на свой вопрос:
 *
 *  1. МИКРОФОН — браузер вообще слышит человека? Показывается живой уровень:
 *     диспетчер видит, что полоска шевелится от голоса, ещё до записи.
 *  2. НАУШНИКИ — записанное слышно обратно? Запись играется в ВЫБРАННОЕ
 *     устройство вывода, а не в системное: у гарнитур это разные вещи, и
 *     «звука нет» чаще всего именно про это.
 *  3. СЕТЬ — браузер знает свой публичный адрес? Без него станция шлёт звук
 *     на локальный адрес ноутбука, и разговор идёт в тишине с обеих сторон.
 *     Прецедент 28.08.2026 — ровно этот случай.
 *
 * Итог проверки уходит на сервер: разбирать «у меня не слышно» по переписке
 * невозможно, а в отчёте видно и устройства, и типы собранных кандидатов.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../shared/api/client.js';
import { Button } from '../../shared/ui/button.js';

/** Сколько пишем голос. Меньше трёх секунд человек не успевает сказать фразу */
const RECORD_MS = 4000;

/**
 * Потолок ожидания сетевой проверки.
 *
 * Совпадает с тем, что софтфон отводит на сбор кандидатов перед звонком:
 * проверка обязана мерить то же самое, иначе она зелёная, а звонок молчит.
 */
const ICE_PROBE_MS = 5000;

type Stage = 'idle' | 'mic' | 'recording' | 'playing' | 'done';

/** Что удалось собрать: по типам кандидатов видно, дошёл ли браузер до STUN */
type NetworkProbe = {
  host: number;
  srflx: number;
  relay: number;
  elapsedMs: number;
};

export interface AudioCheckProps {
  /** Выбранный микрофон; пусто — системный по умолчанию */
  inputDeviceId: string | null;
  /** Выбранные наушники; пусто — системные по умолчанию */
  outputDeviceId: string | null;
}

export function AudioCheck({ inputDeviceId, outputDeviceId }: AudioCheckProps): React.JSX.Element {
  const [stage, setStage] = useState<Stage>('idle');
  const [level, setLevel] = useState(0);
  const [peakLevel, setPeakLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [network, setNetwork] = useState<NetworkProbe | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  /**
   * Пик громкости хранится ещё и ссылкой.
   *
   * Состояние для этого не годится: обработчик проверки замыкает значение
   * на момент своего создания, и на сервер уезжал пик ДО того, как человек
   * заговорил. В отчёте стояло 2% при 100% на экране — то есть диагностика
   * врала ровно там, где её и читают.
   */
  const peakRef = useRef(0);

  /** Всё, что открыли, обязано закрыться: иначе микрофон остаётся занятым и горит индикатор */
  const cleanup = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => { t.stop(); });
    streamRef.current = null;
    void audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
      if (blobUrlRef.current !== null) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [cleanup]);

  /**
   * Сетевая проверка. Отдельное соединение, звонок не трогаем: цель — узнать,
   * какие адреса браузер вообще способен собрать в этой сети.
   */
  const probeNetwork = useCallback(async (): Promise<NetworkProbe> => {
    const config = await api.get<{ iceServers: RTCIceServer[] }>('/desk/softphone-config');
    const started = Date.now();
    const pc = new RTCPeerConnection({ iceServers: config.iceServers });
    const found: NetworkProbe = { host: 0, srflx: 0, relay: 0, elapsedMs: 0 };

    try {
      pc.createDataChannel('probe');
      await pc.setLocalDescription(await pc.createOffer());

      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, ICE_PROBE_MS);
        pc.onicecandidate = (event) => {
          if (event.candidate === null) {
            clearTimeout(timer);
            resolve();
            return;
          }
          const type = event.candidate.type;
          if (type === 'host') found.host += 1;
          else if (type === 'srflx') found.srflx += 1;
          else if (type === 'relay') found.relay += 1;
        };
      });
    } finally {
      pc.close();
    }

    found.elapsedMs = Date.now() - started;
    return found;
  }, []);

  const run = useCallback(async () => {
    setError(null);
    setNetwork(null);
    setPeakLevel(0);
    peakRef.current = 0;
    setStage('mic');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: inputDeviceId === null ? true : { deviceId: { exact: inputDeviceId } },
        video: false,
      });
      streamRef.current = stream;

      // Живой уровень: человек должен видеть реакцию на голос ДО записи,
      // иначе «проверка прошла» превращается в веру на слово
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = (): void => {
        analyser.getByteTimeDomainData(data);
        // Среднеквадратичное отклонение от тишины: пики шума так не обманут
        let sum = 0;
        for (const v of data) sum += (v - 128) ** 2;
        const rms = Math.sqrt(sum / data.length) / 128;
        const percent = Math.min(100, Math.round(rms * 300));
        setLevel(percent);
        peakRef.current = Math.max(peakRef.current, percent);
        setPeakLevel(peakRef.current);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      // Сеть проверяется параллельно записи: она ничего не занимает и
      // не мешает, а диспетчеру не надо ждать её отдельно
      const networkPromise = probeNetwork();

      setStage('recording');
      const chunks: Blob[] = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };

      const recorded = new Promise<Blob>((resolve) => {
        recorder.onstop = () => { resolve(new Blob(chunks, { type: recorder.mimeType })); };
      });
      recorder.start();
      await new Promise((resolve) => setTimeout(resolve, RECORD_MS));
      recorder.stop();
      const blob = await recorded;

      setStage('playing');
      if (blobUrlRef.current !== null) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = URL.createObjectURL(blob);

      const audio = new Audio(blobUrlRef.current);
      audioRef.current = audio;
      // Играем в ВЫБРАННЫЕ наушники. Без этого запись уходит в системное
      // устройство, и проверка молчит именно там, где должна была помочь
      if (outputDeviceId !== null && 'setSinkId' in audio) {
        await (audio as HTMLAudioElement & { setSinkId(id: string): Promise<void> })
          .setSinkId(outputDeviceId)
          .catch(() => undefined);
      }
      await audio.play();
      await new Promise<void>((resolve) => { audio.onended = () => { resolve(); }; });

      const probe = await networkPromise;
      setNetwork(probe);
      setStage('done');

      // Итог на сервер: без него разбор «у меня не слышно» идёт по переписке
      void api
        .post('/desk/audio-check', {
          micPeakPercent: peakRef.current,
          hostCandidates: probe.host,
          srflxCandidates: probe.srflx,
          relayCandidates: probe.relay,
          iceElapsedMs: probe.elapsedMs,
          inputDeviceId,
          outputDeviceId,
          userAgent: navigator.userAgent,
        })
        .catch(() => undefined);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setStage('idle');
    } finally {
      cleanup();
    }
  }, [cleanup, inputDeviceId, outputDeviceId, probeNetwork]);

  const micVerdict = peakLevel >= 8;
  const netVerdict = network === null ? null : network.srflx > 0 || network.relay > 0;

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-slate-900">Проверка звука</h2>
          <p className="text-sm text-slate-600">
            Скажите что-нибудь в микрофон — запишем четыре секунды и сразу проиграем обратно.
            Клиенту никто не звонит.
          </p>
        </div>
        <Button
          variant="primary"
          busy={stage !== 'idle' && stage !== 'done'}
          disabled={stage !== 'idle' && stage !== 'done'}
          onClick={() => { void run(); }}
        >
          {stage === 'done' ? 'Проверить ещё раз' : 'Проверить звук'}
        </Button>
      </div>

      {stage === 'recording' || stage === 'mic' ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-slate-700">
            {stage === 'mic' ? 'Открываю микрофон…' : 'Говорите — идёт запись'}
          </p>
          <div className="h-4 w-full overflow-hidden rounded bg-slate-100">
            <div
              className={`h-full transition-[width] duration-75 ${level >= 8 ? 'bg-emerald-500' : 'bg-slate-300'}`}
              style={{ width: `${String(level)}%` }}
            />
          </div>
          <p className="text-xs text-slate-500">
            Полоска не двигается от голоса — микрофон не тот или он выключен в системе.
          </p>
        </div>
      ) : null}

      {stage === 'playing' ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          Проигрываю запись. Слышите свой голос — наушники работают.
        </p>
      ) : null}

      {error !== null ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          Проверка не прошла: {error}. Чаще всего это запрет доступа к микрофону в браузере.
        </p>
      ) : null}

      {stage === 'done' ? (
        <dl className="flex flex-col gap-2 text-sm">
          <Row
            ok={micVerdict}
            title="Микрофон"
            good={`Слышит голос, пик ${String(peakLevel)}%`}
            bad="Звук не пришёл. Проверьте, тот ли микрофон выбран и не выключен ли он в системе"
          />
          <Row
            ok={netVerdict ?? false}
            title="Сеть"
            good={
              network === null
                ? ''
                : `Публичный адрес получен за ${String(network.elapsedMs)} мс` +
                  (network.relay > 0 ? ', есть запасной канал' : '')
            }
            bad="Браузер не узнал свой публичный адрес. Звонки будут без звука — покажите это сообщение администратору"
          />
          <p className="text-slate-600">
            Голос в записи не услышали? Тогда дело в наушниках: выберите другое устройство вывода
            и проверьте ещё раз.
          </p>
        </dl>
      ) : null}
    </section>
  );
}

function Row({
  ok,
  title,
  good,
  bad,
}: {
  ok: boolean;
  title: string;
  good: string;
  bad: string;
}): React.JSX.Element {
  return (
    <div className="flex gap-3">
      <dt className={`w-28 shrink-0 font-medium ${ok ? 'text-emerald-700' : 'text-rose-700'}`}>
        {ok ? '✓' : '✕'} {title}
      </dt>
      <dd className="text-slate-700">{ok ? good : bad}</dd>
    </div>
  );
}
